/**
 * View Compiler
 * 
 * Transforms CIG and SIB into CompiledViews for rendering by the CockpitRuntime.
 * Each tab gets its specific view structure compiled from the canonical data.
 * 
 * IMPORTANT: The SIB (Structured Intelligence Blocks) contains the rich, interactive
 * components like charts, metric_cards, comparison matrices, risk matrices, timelines.
 * The CIG provides the raw data (entities, claims, relationships, sources).
 */

import {
  CIG,
  CompiledViews,
  CockpitView,
  WrittenReportView,
  TakeawaysView,
  DataFiguresView,
  ConnectionsView,
  SourcesMethodsView,
  Block,
  DEFAULT_TAB_CONFIG,
  Persona,
  DEFAULT_PERSONA,
  Figure
} from '../schemas'

// SIB types from backend — all 62 SIBPackage fields.
// Using Record<string, any> as base allows any SIB type to flow through.
export interface SIBData extends Record<string, any> {
  schema_version?: string
  research_title?: string
  global_blocks?: any[]
  // Metrics
  metric_cards?: any[]
  key_insights_cards?: any[]
  stats_highlights?: any[]
  kpi_dashboards?: any[]
  rating_scorecards?: any[]
  data_confidence_indicators?: any[]
  watchlist_items?: any[]
  // Visualizations
  charts?: any[]
  maps?: any[]
  image_galleries?: any[]
  comparison_matrix?: any
  feature_comparisons?: any[]
  positioning_matrices?: any[]
  data_tables?: any[]
  calculators?: any[]
  // Temporal
  timeline?: any
  timelines?: any[]  // plural fallback
  event_calendars?: any[]
  forecast_cones?: any[]
  // Analysis
  scenario_comparison?: any
  scenario_comparisons?: any[]  // plural fallback
  swot_analyses?: any[]
  pestel_analyses?: any[]
  risk_matrices?: any[]
  sensitivity_analyses?: any[]
  pros_cons_lists?: any[]
  porters_five_forces?: any[]
  moat_analyses?: any[]
  decision_matrices?: any[]
  counterfactual_analyses?: any[]
  hypothesis_test_results?: any[]
  benchmark_comparisons?: any[]
  before_after_comparisons?: any[]
  tam_sam_som_breakdowns?: any[]
  market_segmentations?: any[]
  investment_theses?: any[]
  valuation_comps?: any[]
  funding_histories?: any[]
  unit_economics?: any[]
  revenue_breakdowns?: any[]
  esg_scorecards?: any[]
  sentiment_gauges?: any[]
  trend_radars?: any[]
  customer_journey_maps?: any[]
  org_charts?: any[]
  compliance_checklists?: any[]
  cohort_analyses?: any[]
  supply_chain_maps?: any[]
  partnership_networks?: any[]
  quote_galleries?: any[]
  action_items?: any[]
  assumptions_logs?: any[]
  process_flows?: any[]
  citation_networks?: any[]
  faq_accordions?: any[]
  glossaries?: any[]
  // Structure
  entity_graph?: any
  persona_modules?: any[]
  cockpit?: {
    status_strip?: any
    signal_cards?: any[]
    hero_viz_frame?: any
    verdict?: any
    watchlist?: any
  }
  tabs?: Record<string, string[]>
  sources_summary?: any[]
}

export interface DeepResearchData {
  text?: string
  citations?: any[]
  sources?: any[]
  status?: string
  provider?: string
}

const isSyntheticSourceId = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  (value.startsWith('FALLBACK_') || value.startsWith('AUTO_') || value.startsWith('timeline_'))

function normalizeTimelineAnchors(event: any): any[] {
  const rawAnchors = Array.isArray(event?.evidence_anchors)
    ? event.evidence_anchors
    : Array.isArray(event?.anchors)
      ? event.anchors
      : []

  return rawAnchors
    .filter((anchor: any) => anchor && typeof anchor === 'object')
    .map((anchor: any) => ({
      source_id: anchor.source_id,
      source_url: anchor.source_url,
      locator: anchor.locator || anchor.location,
      snippet: anchor.snippet || anchor.text,
      support_type: anchor.support_type || 'context',
      reliability_score: anchor.reliability_score,
      timestamp: anchor.timestamp || anchor.captured_at,
    }))
    .filter((anchor: any) =>
      (typeof anchor.source_id === 'string' && anchor.source_id.length > 0) ||
      (typeof anchor.source_url === 'string' && anchor.source_url.trim().length > 0)
    )
}

function getTimelineSourceIds(event: any, anchors: any[] = normalizeTimelineAnchors(event)): string[] {
  const seen = new Set<string>()
  const refs: string[] = []

  const pushRef = (value: unknown) => {
    if (typeof value !== 'string') return
    const sourceId = value.trim()
    if (!sourceId || isSyntheticSourceId(sourceId) || seen.has(sourceId)) {
      return
    }

    seen.add(sourceId)
    refs.push(sourceId)
  }

  anchors.forEach((anchor: any) => {
    pushRef(anchor?.source_id)
  })

  if (Array.isArray(event?.source_ids)) {
    event.source_ids.forEach(pushRef)
  }

  pushRef(event?.source_id)

  return refs
}

function getTimelineCitationRefs(event: any): string[] {
  return getTimelineSourceIds(event)
}

function hasAuthoritativeTimelineSource(event: any, anchors: any[] = normalizeTimelineAnchors(event)): boolean {
  if (getTimelineSourceIds(event, anchors).length > 0) {
    return true
  }

  return anchors.some((anchor: any) =>
    typeof anchor?.source_url === 'string' && anchor.source_url.trim().length > 0
  )
}

function normalizeTimelineEvent(event: any, index: number): any {
  const evidenceAnchors = normalizeTimelineAnchors(event)
  const sourceIds = getTimelineSourceIds(event, evidenceAnchors)

  return {
    ...event,
    id: event?.id || event?.event_id || `timeline-event-${index + 1}`,
    evidence_anchors: evidenceAnchors,
    anchors: evidenceAnchors,
    ...(sourceIds.length > 0 ? { source_ids: sourceIds } : {}),
  }
}

/**
 * Compile CIG and SIB into views for all tabs
 * @param cig - Canonical Intelligence Graph (entities, claims, relationships, sources)
 * @param persona - Target persona for view customization
 * @param sib - Structured Intelligence Blocks (charts, metrics, matrices, etc.)
 * @param deepResearch - Raw deep research artifact (markdown + citations)
 */
export function compileViews(
  cig: CIG,
  persona: Persona = DEFAULT_PERSONA,
  sib?: SIBData,
  deepResearch?: DeepResearchData
): CompiledViews {
  const cockpitView = compileCockpitView(cig, persona, sib)
  const writtenReportView = compileWrittenReportView(cig, persona, sib, deepResearch)
  const takeawaysView = compileTakeawaysView(cig, persona, sib)
  const dataFiguresView = compileDataFiguresView(cig, persona, sib)
  const connectionsView = compileConnectionsView(cig, persona, sib)
  const sourcesMethodsView = compileSourcesMethodsView(cig, persona, sib)

  const tabs = DEFAULT_TAB_CONFIG.map(tab => {
    if (tab.id === 'connections') {
      // Show Knowledge Graph tab if there are either entities (nodes) OR relationships (edges)
      return { ...tab, enabled: connectionsView.nodes.length > 0 || connectionsView.edges.length > 0 }
    }
    if (tab.id === 'data_figures') {
      return { ...tab, enabled: hasDataFigures(dataFiguresView) }
    }
    if (tab.id === 'takeaways') {
      return { ...tab, enabled: false }
    }
    return tab
  })

  return {
    schema_version: '2.2',
    report_id: cig.metadata.report_id,
    tabs,
    cockpit: cockpitView,
    written_report: writtenReportView,
    takeaways: takeawaysView,
    data_figures: dataFiguresView,
    connections: connectionsView,
    sources_methods: sourcesMethodsView
  }
}

/**
 * Compile Cockpit view - USES SIB DATA for rich components
 */
function compileCockpitView(cig: CIG, persona: Persona, sib?: SIBData): CockpitView {
  // Using any[] and casting later since Block discriminated union is strict
  const signals: any[] = []
  const blocks: Block[] = []
  
  // ======= PRIORITY 1: Use SIB data if available =======
  if (sib) {
    // eslint-disable-next-line no-console
    console.log('[viewCompiler] SIB data received:', {
      hasCharts: !!sib.charts,
      chartsLength: sib.charts?.length,
      hasMaps: !!sib.maps,
      mapsLength: sib.maps?.length,
      hasComparisonMatrix: !!sib.comparison_matrix,
      hasRiskMatrices: !!sib.risk_matrices,
      riskMatricesLength: sib.risk_matrices?.length,
      hasSwotAnalyses: !!sib.swot_analyses,
      swotAnalysesLength: sib.swot_analyses?.length,
      hasQuoteGalleries: !!sib.quote_galleries,
      quoteGalleriesLength: sib.quote_galleries?.length,
      hasRatingScorecards: !!sib.rating_scorecards,
      ratingScorecardsLength: sib.rating_scorecards?.length,
      hasDataTables: !!sib.data_tables,
      dataTablesLength: sib.data_tables?.length,
      // Check both singular and plural versions
      hasTimeline: !!sib.timeline,
      hasTimelines: !!sib.timelines,
      timelineEventCount: sib.timeline?.events?.length || sib.timelines?.[0]?.events?.length || 0,
      hasSensitivityAnalyses: !!sib.sensitivity_analyses,
      sensitivityAnalysesLength: sib.sensitivity_analyses?.length,
      hasScenarioComparison: !!sib.scenario_comparison,
      hasScenarioComparisons: !!sib.scenario_comparisons,
      hasCockpit: !!sib.cockpit,
      sibKeys: Object.keys(sib as any),
    })
    
    // Use SIB signal cards from cockpit or global_blocks
    const sibSignals = sib.cockpit?.signal_cards || 
      sib.global_blocks?.filter((b: any) => b.block_type === 'signal') || []
    
    for (const sig of sibSignals.slice(0, 8)) {
      signals.push({
        id: sig.block_id || `signal-${signals.length}`,
        type: 'signal_card',
        title: sig.title,
        collapsed: false,
        data: {
          variant: 'insight',
          summary: sig.summary,
          trend: sig.payload?.trend,
          confidence: sig.payload?.confidence || 'medium',
          evidence_anchors: sig.anchors?.map((a: any) => ({
            source_id: a.source_id,
            snippet: a.snippet,
            support_type: 'supports' as const
          })) || []
        }
      })
    }
    
    // Add SIB metric cards as signals
    if (sib.metric_cards && sib.metric_cards.length > 0) {
      for (const mc of sib.metric_cards.slice(0, 6)) {
        const evidenceAnchors = (mc.anchors || [])
          .map((anchor: any) => ({
            source_id: anchor?.source_id,
            snippet: anchor?.snippet || anchor?.text,
            support_type: (anchor?.support_type || 'supports') as 'supports' | 'contradicts' | 'context'
          }))
          .filter((anchor: any) => Boolean(anchor.source_id))

        signals.push({
          id: `metric-${mc.metric_id}`,
          type: 'signal_card',
          title: mc.title,
          collapsed: false,
          data: {
            variant: 'numeric',
            value: mc.value,
            unit: mc.unit,
            context: mc.context,
            trend: mc.trend,
            sparkline_data: mc.sparkline_data,
            comparison: mc.comparison,
            confidence: 'high',
            evidence_anchors: evidenceAnchors
          }
        })
      }
    }
    
    // Add SIB charts as viz blocks
    if (sib.charts && sib.charts.length > 0) {
      for (const chart of sib.charts) {
        blocks.push({
          id: chart.chart_id,
          type: 'viz_frame',
          title: chart.title,
          collapsed: false,
          data: {
            renderer: chart.chart_type,
            figure_id: chart.chart_id,
            inline_data: {
              type: chart.chart_type,
              series: chart.series,
              interactive: chart.interactive
            },
            caption: chart.title
          }
        } as Block)
      }
    }
    
    // Add SIB maps as viz_frame blocks with map renderer
    if (sib.maps && sib.maps.length > 0) {
      console.log('[viewCompiler] Processing SIB maps:', sib.maps.length)
      for (const map of sib.maps) {
        blocks.push({
          id: map.map_id || `map-${blocks.length}`,
          type: 'viz_frame',
          title: map.title,
          collapsed: false,
          data: {
            renderer: 'map',
            figure_id: map.map_id,
            inline_data: {
              map_id: map.map_id,
              map_type: map.map_type || 'point',
              title: map.title,
              subtitle: map.subtitle,
              focus_location: map.focus_location,
              base_map: map.base_map,
              points: map.points || [],
              regions: map.regions || [],
              color_scale: map.color_scale,
              legend_title: map.legend_title,
              zoom_enabled: map.zoom_enabled ?? true,
              map_style: map.map_style || 'light'
            },
            caption: map.title
          }
        } as Block)
      }
    }
    
    // Add SIB comparison matrix - using generic_sib for flexible rendering
    if (sib.comparison_matrix) {
      console.log('[viewCompiler] Processing comparison matrix:', sib.comparison_matrix.title)
      blocks.push({
        id: sib.comparison_matrix.matrix_id || 'comparison-matrix',
        type: 'generic_sib',
        title: sib.comparison_matrix.title || 'Comparison Matrix',
        collapsed: false,
        data: {
          sib_type: 'comparison_matrix', sib_category: 'comparisons',
          content: {
            row_headers: sib.comparison_matrix.row_headers,
            column_headers: sib.comparison_matrix.column_headers,
            cells: sib.comparison_matrix.cells
          }
        }
      } as Block)
    }
    
    // Add SIB risk matrices
    if (sib.risk_matrices && sib.risk_matrices.length > 0) {
      for (const rm of sib.risk_matrices) {
        blocks.push({
          id: rm.matrix_id,
          type: 'risk_matrix',
          title: rm.title,
          collapsed: false,
          data: {
            risks: rm.risks
          }
        } as Block)
      }
    }
    
    // Add SIB rating scorecards
    if (sib.rating_scorecards && sib.rating_scorecards.length > 0) {
      for (const sc of sib.rating_scorecards) {
        blocks.push({
          id: sc.scorecard_id,
          type: 'rating_scorecard',
          title: sc.title,
          collapsed: false,
          data: {
            subject: sc.subject,
            dimensions: sc.dimensions,
            overall_score: sc.overall_score,
            overall_max: sc.overall_max,
            grade: sc.grade
          }
        } as Block)
      }
    }
    
    // Add SIB data tables
    if (sib.data_tables && sib.data_tables.length > 0) {
      for (const dt of sib.data_tables) {
        blocks.push({
          id: dt.table_id,
          type: 'data_table',
          title: dt.title,
          collapsed: false,
          data: {
            columns: dt.columns?.map((c: any) => ({
              key: c.column_id || c.key,
              label: c.header || c.label,
              type: c.data_type || c.type || 'string',
              sortable: true,
              filterable: true
            })),
            rows: dt.rows?.map((r: any) => r.cells || r),
            page_size: dt.page_size || 10,
            enable_export: dt.exportable ?? true
          }
        } as Block)
      }
    }
    
    // Add SIB timelines - handle both singular 'timeline' (backend) and plural 'timelines' (fallback)
    const timelineList = sib.timelines || (sib.timeline ? [sib.timeline] : [])
    if (timelineList.length > 0) {
      for (const tl of timelineList) {
        const normalizedEvents = (tl.events || []).map((event: any, index: number) =>
          normalizeTimelineEvent(event, index)
        ).filter((event: any) => hasAuthoritativeTimelineSource(event, event?.anchors || event?.evidence_anchors || []))

        if (normalizedEvents.length === 0) {
          continue
        }

        blocks.push({
          id: tl.timeline_id || 'timeline-1',
          type: 'timeline',
          title: tl.title,
          collapsed: false,
          data: {
            events: normalizedEvents
          }
        } as Block)
      }
    }
    
    // Add SIB scenario comparisons - handle both singular (backend) and plural (fallback)
    const scenarioList = sib.scenario_comparisons || (sib.scenario_comparison ? [sib.scenario_comparison] : [])
    if (scenarioList.length > 0) {
      for (const sc of scenarioList) {
        blocks.push({
          id: sc.comparison_id || 'scenario-comparison-1',
          type: 'scenario_comparison',
          title: sc.title,
          collapsed: false,
          data: {
            scenarios: sc.scenarios,
            axis_id: sc.axis_id
          }
        } as Block)
      }
    }
    
    // Add SIB sensitivity analyses
    if (sib.sensitivity_analyses && sib.sensitivity_analyses.length > 0) {
      for (const sa of sib.sensitivity_analyses) {
        blocks.push({
          id: sa.analysis_id,
          type: 'sensitivity_analysis',
          title: sa.title,
          collapsed: false,
          data: {
            factors: sa.factors,
            baseline_state_id: sa.baseline_state_id
          }
        } as Block)
      }
    }
    
    // ======= NEW SIB TYPES - Using generic_sib block type =======
    // These use GenericSIBRenderer for flexible rendering
    
    // Add SIB SWOT analyses (single analysis only - backend prompt generates one)
    if (sib.swot_analyses && sib.swot_analyses.length > 0) {
      const swot = sib.swot_analyses[0]
      blocks.push({
        id: swot.analysis_id || swot.swot_id || 'swot-1',
        type: 'generic_sib',
        title: swot.title || swot.subject || 'SWOT Analysis',
        collapsed: false,
        data: {
          sib_type: 'swot_analyses', sib_category: 'frameworks',
          content: swot
        }
      } as Block)
    }
    
    // Add SIB quote galleries
    if (sib.quote_galleries && sib.quote_galleries.length > 0) {
      console.log('[viewCompiler] Processing quote galleries:', sib.quote_galleries.length)
      for (const qg of sib.quote_galleries) {
        blocks.push({
          id: qg.gallery_id || `quotes-${blocks.length}`,
          type: 'generic_sib',
          title: qg.title || 'Expert Quotes',
          collapsed: false,
          data: {
            sib_type: 'quote_galleries', sib_category: 'insights',
            content: qg
          }
        } as Block)
      }
    }
    
    // Add SIB PESTEL analyses (single analysis only - backend prompt generates one)
    if (sib.pestel_analyses && sib.pestel_analyses.length > 0) {
      const pestel = sib.pestel_analyses[0]
      blocks.push({
        id: pestel.analysis_id || 'pestel-1',
        type: 'generic_sib',
        title: pestel.title || pestel.subject || 'PESTEL Analysis',
        collapsed: false,
        data: {
          sib_type: 'pestel_analyses', sib_category: 'frameworks',
          content: pestel
        }
      } as Block)
    }
    
    // Add SIB key insights cards
    if (sib.key_insights_cards && sib.key_insights_cards.length > 0) {
      console.log('[viewCompiler] Processing key insights cards:', sib.key_insights_cards.length)
      for (const kic of sib.key_insights_cards) {
        blocks.push({
          id: kic.card_id || `insights-${blocks.length}`,
          type: 'generic_sib',
          title: kic.title || 'Key Insights',
          collapsed: false,
          data: {
            sib_type: 'key_insights_cards', sib_category: 'insights',
            content: kic
          }
        } as Block)
      }
    }
    
    // Add SIB action items
    if (sib.action_items && sib.action_items.length > 0) {
      console.log('[viewCompiler] Processing action items:', sib.action_items.length)
      for (const ai of sib.action_items) {
        blocks.push({
          id: ai.actions_id || `actions-${blocks.length}`,
          type: 'generic_sib',
          title: ai.title || 'Action Items',
          collapsed: false,
          data: {
            sib_type: 'action_items', sib_category: 'insights',
            content: ai
          }
        } as Block)
      }
    }
    
    // Add SIB assumptions logs
    if (sib.assumptions_logs && sib.assumptions_logs.length > 0) {
      console.log('[viewCompiler] Processing assumptions logs:', sib.assumptions_logs.length)
      for (const al of sib.assumptions_logs) {
        blocks.push({
          id: al.log_id || `assumptions-${blocks.length}`,
          type: 'generic_sib',
          title: al.title || 'Key Assumptions',
          collapsed: false,
          data: {
            sib_type: 'assumptions_logs', sib_category: 'insights',
            content: al
          }
        } as Block)
      }
    }
    
    // Add SIB org charts
    if (sib.org_charts && sib.org_charts.length > 0) {
      console.log('[viewCompiler] Processing org charts:', sib.org_charts.length)
      for (const oc of sib.org_charts) {
        blocks.push({
          id: oc.chart_id || `org-${blocks.length}`,
          type: 'generic_sib',
          title: oc.title || 'Organization Structure',
          collapsed: false,
          data: {
            sib_type: 'org_charts', sib_category: 'operations',
            content: oc
          }
        } as Block)
      }
    }
  }
  
  // ======= ALL REMAINING SIB TYPES — route to generic_sib blocks =======
  // Category map for grouping SIBs into logical sections (shared with mobile)
  const SIB_CATEGORY_MAP: Record<string, string> = {
    // Strategic Frameworks
    swot_analyses: 'frameworks', pestel_analyses: 'frameworks', porters_five_forces: 'frameworks',
    moat_analyses: 'frameworks', pros_cons_lists: 'frameworks', decision_matrices: 'frameworks',
    counterfactual_analyses: 'frameworks',
    // Financial Analysis
    investment_theses: 'financial', valuation_comps: 'financial', funding_histories: 'financial',
    unit_economics: 'financial', revenue_breakdowns: 'financial', tam_sam_som_breakdowns: 'financial',
    benchmark_comparisons: 'financial', before_after_comparisons: 'financial',
    forecast_cone: 'financial', forecast_cones: 'financial',
    calculator: 'financial', calculators: 'financial',
    // Comparisons & Positioning
    comparison_matrix: 'comparisons', feature_comparisons: 'comparisons', positioning_matrices: 'comparisons',
    sensitivity_analyses: 'comparisons', cohort_analyses: 'comparisons', hypothesis_test_results: 'comparisons',
    // Ratings & Scores
    esg_scorecards: 'ratings', sentiment_gauges: 'ratings', trend_radars: 'ratings',
    rating_scorecards: 'ratings', compliance_checklists: 'ratings',
    // Operations & Networks
    org_charts: 'operations', supply_chain_maps: 'operations', partnership_networks: 'operations',
    process_flows: 'operations', citation_networks: 'operations', customer_journey_maps: 'operations',
    market_segmentations: 'operations',
    // Insights & Recommendations
    quote_galleries: 'insights', action_items: 'insights', assumptions_logs: 'insights',
    faq_accordions: 'insights', image_galleries: 'insights',
    event_calendars: 'insights',
  }

  const genericSibTypes: Array<{ field: string; sibType: string; getId: (item: any) => string; getTitle: (item: any) => string }> = [
    { field: 'pros_cons_lists', sibType: 'pros_cons_lists', getId: (i) => i.list_id, getTitle: (i) => i.title || 'Pros & Cons' },
    { field: 'porters_five_forces', sibType: 'porters_five_forces', getId: (i) => i.analysis_id, getTitle: (i) => i.title || "Porter's Five Forces" },
    { field: 'moat_analyses', sibType: 'moat_analyses', getId: (i) => i.moat_id, getTitle: (i) => i.title || 'Competitive Moat' },
    { field: 'investment_theses', sibType: 'investment_theses', getId: (i) => i.thesis_id, getTitle: (i) => i.title || 'Investment Thesis' },
    { field: 'valuation_comps', sibType: 'valuation_comps', getId: (i) => i.comps_id, getTitle: (i) => i.title || 'Valuation Comparables' },
    { field: 'funding_histories', sibType: 'funding_histories', getId: (i) => i.history_id, getTitle: (i) => i.title || 'Funding History' },
    { field: 'unit_economics', sibType: 'unit_economics', getId: (i) => i.economics_id, getTitle: (i) => i.title || 'Unit Economics' },
    { field: 'revenue_breakdowns', sibType: 'revenue_breakdowns', getId: (i) => i.breakdown_id, getTitle: (i) => i.title || 'Revenue Breakdown' },
    { field: 'tam_sam_som_breakdowns', sibType: 'tam_sam_som_breakdowns', getId: (i) => i.breakdown_id, getTitle: (i) => i.title || 'TAM/SAM/SOM' },
    { field: 'market_segmentations', sibType: 'market_segmentations', getId: (i) => i.segmentation_id, getTitle: (i) => i.title || 'Market Segmentation' },
    { field: 'benchmark_comparisons', sibType: 'benchmark_comparisons', getId: (i) => i.benchmark_id, getTitle: (i) => i.title || 'Benchmark Comparison' },
    { field: 'before_after_comparisons', sibType: 'before_after_comparisons', getId: (i) => i.comparison_id, getTitle: (i) => i.title || 'Before & After' },
    { field: 'feature_comparisons', sibType: 'feature_comparisons', getId: (i) => i.table_id, getTitle: (i) => i.title || 'Feature Comparison' },
    { field: 'positioning_matrices', sibType: 'positioning_matrices', getId: (i) => i.matrix_id, getTitle: (i) => i.title || 'Positioning Matrix' },
    { field: 'decision_matrices', sibType: 'decision_matrices', getId: (i) => i.matrix_id, getTitle: (i) => i.title || 'Decision Matrix' },
    { field: 'esg_scorecards', sibType: 'esg_scorecards', getId: (i) => i.scorecard_id, getTitle: (i) => i.title || 'ESG Scorecard' },
    { field: 'sentiment_gauges', sibType: 'sentiment_gauges', getId: (i) => i.gauge_id, getTitle: (i) => i.title || 'Sentiment Analysis' },
    { field: 'trend_radars', sibType: 'trend_radars', getId: (i) => i.radar_id, getTitle: (i) => i.title || 'Trend Radar' },
    { field: 'customer_journey_maps', sibType: 'customer_journey_maps', getId: (i) => i.journey_id, getTitle: (i) => i.title || 'Customer Journey' },
    { field: 'compliance_checklists', sibType: 'compliance_checklists', getId: (i) => i.checklist_id, getTitle: (i) => i.title || 'Compliance Checklist' },
    { field: 'cohort_analyses', sibType: 'cohort_analyses', getId: (i) => i.analysis_id, getTitle: (i) => i.title || 'Cohort Analysis' },
    { field: 'hypothesis_test_results', sibType: 'hypothesis_test_results', getId: (i) => i.results_id, getTitle: (i) => i.title || 'Hypothesis Tests' },
    { field: 'supply_chain_maps', sibType: 'supply_chain_maps', getId: (i) => i.map_id, getTitle: (i) => i.title || 'Supply Chain' },
    { field: 'partnership_networks', sibType: 'partnership_networks', getId: (i) => i.network_id, getTitle: (i) => i.title || 'Partnership Network' },
    { field: 'counterfactual_analyses', sibType: 'counterfactual_analyses', getId: (i) => i.analysis_id, getTitle: (i) => i.title || 'Counterfactual Analysis' },
    { field: 'process_flows', sibType: 'process_flows', getId: (i) => i.flow_id, getTitle: (i) => i.title || 'Process Flow' },
    { field: 'citation_networks', sibType: 'citation_networks', getId: (i) => i.network_id, getTitle: (i) => i.title || 'Citation Network' },
    { field: 'forecast_cones', sibType: 'forecast_cones', getId: (i) => i.forecast_id, getTitle: (i) => i.title || 'Forecast' },
    { field: 'event_calendars', sibType: 'event_calendars', getId: (i) => i.calendar_id, getTitle: (i) => i.title || 'Event Calendar' },
    { field: 'faq_accordions', sibType: 'faq_accordions', getId: (i) => i.faq_id, getTitle: (i) => i.title || 'FAQ' },
    { field: 'image_galleries', sibType: 'image_galleries', getId: (i) => i.gallery_id, getTitle: (i) => i.title || 'Image Gallery' },
    { field: 'calculators', sibType: 'calculators', getId: (i) => i.calculator_id, getTitle: (i) => i.title || 'Calculator' },
  ]

  if (sib) {
    for (const { field, sibType, getId, getTitle } of genericSibTypes) {
      const sibField = (sib as any)[field]
      if (!sibField) continue
      const items = Array.isArray(sibField) ? sibField : [sibField]
      for (const item of items) {
        if (!item || (typeof item === 'object' && Object.keys(item).length === 0)) continue
        blocks.push({
          id: getId(item) || `${sibType}-${blocks.length}`,
          type: 'generic_sib',
          title: getTitle(item),
          collapsed: false,
          data: { sib_type: sibType, sib_category: SIB_CATEGORY_MAP[sibType] || 'insights', content: item }
        } as Block)
      }
    }
  }

  // ======= FALLBACK: Supplement with CIG data if too few SIB signals =======
  // Aim for at least 5 signals for a good user experience
  const MIN_SIGNALS = 5
  
  if (signals.length < MIN_SIGNALS) {
    // Add metrics from CIG if available
    const topMetrics = cig.metrics?.slice(0, MIN_SIGNALS - signals.length) || []
    const existingTitles = new Set(signals.map(s => (s.title || '').toLowerCase()))
    
    for (const metric of topMetrics) {
      // Don't add duplicates
      if (existingTitles.has((metric.label || '').toLowerCase())) continue
      
      signals.push({
        id: `signal-${metric.id}`,
        type: 'signal_card',
        title: metric.label,
        collapsed: false,
        data: {
          variant: 'numeric',
          value: metric.value,
          unit: metric.unit,
          timeframe: metric.timeframe,
          trend: metric.trend,
          trend_value: metric.trend_value,
          confidence: metric.confidence,
          evidence_anchors: metric.evidence_anchors
        }
      })
    }
    
    // Add key claims as signals if still not enough
    if (signals.length < MIN_SIGNALS && cig.claims) {
      const topClaims = cig.claims.slice(0, MIN_SIGNALS - signals.length)
      for (const claim of topClaims) {
        if (existingTitles.has((claim.text || '').toLowerCase().substring(0, 50))) continue
        
        // Create a short title from the claim text
        const claimTitle = (claim.text || '').split(/[.!?]/)[0].trim().substring(0, 80) || 'Key Finding'
        
        signals.push({
          id: `signal-claim-${claim.id}`,
          type: 'signal_card',
          title: claimTitle,
          collapsed: false,
          data: {
            variant: 'insight',
            summary: claim.text,
            confidence: claim.confidence || 'medium',
            evidence_anchors: claim.evidence_anchors || []
          }
        })
      }
    }
    
    // Ultimate fallback: create categorical signals from metadata
    if (signals.length === 0) {
      signals.push({
        id: 'signal-confidence',
        type: 'signal_card',
        title: 'Research Confidence',
        collapsed: false,
        data: {
          variant: 'categorical',
          value: cig.metadata.confidence?.replace('_', ' ') || 'medium',
          color: confidenceToColor(cig.metadata.confidence || 'medium'),
          confidence: cig.metadata.confidence || 'medium',
          evidence_anchors: (cig.sources || []).slice(0, 1).map((s: any) => ({
            source_id: s.id,
            support_type: 'context' as const
          }))
        }
      })
      
      if (cig.metadata.risk_posture) {
        signals.push({
          id: 'signal-risk',
          type: 'signal_card',
          title: 'Risk Posture',
          collapsed: false,
          data: {
            variant: 'categorical',
            value: cig.metadata.risk_posture,
            color: riskToColor(cig.metadata.risk_posture),
            confidence: cig.metadata.confidence || 'medium',
            evidence_anchors: (cig.sources || []).slice(0, 1).map((s: any) => ({
              source_id: s.id,
              support_type: 'context' as const
            }))
          }
        })
      }
      
      signals.push({
        id: 'signal-sources',
        type: 'signal_card',
        title: 'Sources Analyzed',
        collapsed: false,
        data: {
          variant: 'numeric',
          value: (cig.sources || []).length,
          unit: 'sources',
          confidence: cig.metadata.confidence || 'medium',
          evidence_anchors: []
        }
      })
    }
  }

  // Filter out ONLY exact placeholder signals and deduplicate
  const seenTitles = new Set<string>()
  const deduplicatedSignals: typeof signals = []
  
  // Placeholder titles to skip (exact or pattern); Key Signals with no real content get filtered
  const exactPlaceholderTitles = [
    'global trigger',
    'monitor leading indicators',
    'placeholder',
    'tbd',
    'n/a',
    'signal',
    ''
  ]
  const placeholderTitlePattern = /^signal\s*\d+$/i

  for (const signal of signals) {
    const rawTitle = (signal.title || '').trim()
    const title = rawTitle.toLowerCase()

    // Skip exact placeholder titles
    if (exactPlaceholderTitles.includes(title) || title.includes('global trigger')) {
      continue
    }
    // Skip "Signal 1", "Signal 2" etc. when they have no real content (use summary as title if present)
    if (placeholderTitlePattern.test(rawTitle)) {
      const summary = (signal.data as any)?.summary
      if (summary && typeof summary === 'string' && summary.length > 20) {
        // Keep signal but use first sentence of summary as display title
        deduplicatedSignals.push({
          ...signal,
          title: summary.split(/[.!?]/)[0].trim().slice(0, 80) || rawTitle
        })
        seenTitles.add((summary.slice(0, 50) || rawTitle).toLowerCase())
      }
      continue
    }

    // Deduplicate by title
    if (title && seenTitles.has(title)) {
      continue
    }

    seenTitles.add(title)
    deduplicatedSignals.push(signal)
  }
  
  signals.length = 0
  signals.push(...deduplicatedSignals)
  
  console.log('[viewCompiler] Signals after filtering:', deduplicatedSignals.length)

  // Create verdict block - prefer SIB cockpit verdict
  let verdict: any = undefined
  if (sib?.cockpit?.verdict) {
    const sibVerdict = sib.cockpit.verdict
    verdict = {
      id: sibVerdict.block_id || 'verdict-main',
      type: 'verdict',
      collapsed: false,
      data: {
        bluf: sibVerdict.title,
        so_what: sibVerdict.summary,
        confidence: sibVerdict.payload?.confidence || 'medium',
        recommendation: sibVerdict.payload?.recommendation,
        evidence_anchors: sibVerdict.anchors?.map((a: any) => ({
          source_id: a.source_id,
          snippet: a.snippet,
          support_type: 'supports' as const
        })) || []
      }
    }
  } else if (cig.bluf) {
    verdict = {
      id: 'verdict-main',
      type: 'verdict',
      collapsed: false,
      data: {
        bluf: cig.bluf,
        so_what: cig.executive_summary[0] || 'See full report for implications.',
        counterpoint: undefined,
        key_assumption: undefined,
        confidence: cig.metadata.confidence,
        evidence_anchors: (cig.sources || []).slice(0, 3).map((s: any) => ({
          source_id: s.id,
          support_type: 'supports' as const
        }))
      }
    }
  }

  // Create watchlist preview - prefer SIB cockpit watchlist
  // NOTE: Only use real trigger/watchlist data, NOT claims (which are already shown in signals)
  let watchlistPreview: any[] = []
  if (sib?.cockpit?.watchlist?.payload?.items) {
    watchlistPreview = sib.cockpit.watchlist.payload.items.slice(0, 10).map((item: any) => ({
      indicator: item.condition || item.trigger_id,
      status: item.confidence >= 0.7 ? 'warning' : 'normal',
      description: item.condition,
      linked_states: item.linked_states,
      last_known_value: item.last_known_value,
      gap_to_threshold: item.gap_to_threshold_pp || item.buffer_above_threshold_pp
    }))
  } else if (cig.watchlist && cig.watchlist.length > 0) {
    watchlistPreview = cig.watchlist.slice(0, 7).map(item => ({
      indicator: item.indicator,
      status: item.status,
      description: item.description
    }))
  }
  // Removed: fallback to claims - this duplicates signals and causes confusion

  // Create jump chips
  const jumpChips = buildJumpChips(cig)

  // Hero viz - prefer SIB charts, then CIG figures
  let heroViz: Block | undefined = undefined
  if (sib?.charts && sib.charts.length > 0) {
    const firstChart = sib.charts[0]
    heroViz = {
      id: `hero-${firstChart.chart_id}`,
      type: 'viz_frame',
      collapsed: false,
      title: firstChart.title,
      data: {
        renderer: firstChart.chart_type,
        figure_id: firstChart.chart_id,
        inline_data: {
          type: firstChart.chart_type,
          series: firstChart.series,
          interactive: firstChart.interactive
        },
        caption: firstChart.title
      }
    } as Block
  } else if (cig.figures && cig.figures.length > 0) {
    heroViz = convertFigureToVizBlock(cig.figures[0])
  }

  // Merge SIB blocks with CIG-derived blocks
  const cigBlocks = buildAdditionalCockpitBlocks(cig)
  const allBlocks = [...blocks, ...cigBlocks]

  // eslint-disable-next-line no-console
  console.log('[viewCompiler] Final blocks created:', {
    sibBlocksCount: blocks.length,
    sibBlockTypes: blocks.map((b: Block) => b.type),
    cigBlocksCount: cigBlocks.length,
    cigBlockTypes: cigBlocks.map((b: Block) => b.type),
    allBlocksCount: allBlocks.length,
    signalsCount: signals.length,
  })

  return {
    tab: 'cockpit',
    status_strip: {
      risk_posture: sib?.cockpit?.status_strip?.payload?.overall_status || cig.metadata.risk_posture || 'moderate',
      confidence: sib?.cockpit?.status_strip?.payload?.confidence || cig.metadata.confidence,
      last_updated: cig.metadata.last_updated,
      what_changed: cig.change_log?.[0]?.description,
      tags: cig.metadata.tags
    },
    signals,
    hero_viz: heroViz,
    verdict,
    watchlist_preview: watchlistPreview,
    jump_chips: jumpChips,
    blocks: allBlocks
  }
}

/**
 * Compile Written Report view - USES SIB data for rich content
 * 
 * Priority order:
 * 1. CIG report_sections (if available)
 * 2. SIB persona_modules + cockpit.verdict + global_blocks (synthesize into sections)
 * 3. CIG raw_report_markdown (fallback)
 */
function compileWrittenReportView(
  cig: CIG,
  persona: Persona,
  sib?: SIBData,
  deepResearch?: DeepResearchData
): WrittenReportView {
  // Generate footnotes from sources - handle undefined sources
  // Prefer SIB sources_summary if available
  const sources = sib?.sources_summary || cig.sources || []
  const footnotes = sources.map((source: any, index: number) => ({
    id: source.source_id || source.id,
    number: index + 1,
    source_id: source.source_id || source.id,
    text: `${source.title}${source.publisher ? ` - ${source.publisher}` : ''}${source.date ? ` (${source.date})` : ''}${source.url ? ` - ${source.url}` : ''}`,
    reliability: source.reliability
  }))
  const usedFootnoteIds = new Set(footnotes.map(fn => fn.id))
  let nextFootnoteNumber = footnotes.length + 1

  if (Array.isArray(deepResearch?.sources)) {
    const deepResearchSources = Array.isArray(deepResearch?.sources) ? (deepResearch?.sources as any[]) : []
    deepResearchSources.forEach((rawSource: any, index: number) => {
      const normalized = normalizeDeepResearchSource(rawSource, index)
      if (!normalized) return
      if (usedFootnoteIds.has(normalized.id)) return

      footnotes.push({
        id: normalized.id,
        number: nextFootnoteNumber++,
        source_id: normalized.id,
        text: normalized.text,
        reliability: normalized.reliability || {
          score: 0.6,
          tier: 'secondary',
          rationale: 'Deep research supplemental source',
        },
      })
      usedFootnoteIds.add(normalized.id)
    })
  }

  const deepResearchMarkdown = deepResearch?.text?.trim()

  // Build executive summary - prefer SIB cockpit.status_strip.summary
  let executiveSummary = cig.executive_summary.map((text: any) => ({ text }))
  if (executiveSummary.length === 0 && sib?.cockpit?.status_strip?.summary) {
    executiveSummary = [{ text: sib.cockpit.status_strip.summary }]
  }
  
  // Build BLUF - prefer SIB cockpit.verdict
  let bluf = cig.bluf
  if (!bluf && sib?.cockpit?.verdict) {
    bluf = sib.cockpit.verdict.title || sib.cockpit.verdict.summary
  }

  // Build sections from SIB if CIG sections are empty
  let sections = cig.report_sections || []
  
  if (sections.length === 0 && sib) {
    sections = buildSectionsFromSIB(sib)
  }

  // Build implications from SIB persona_modules
  let implications = cig.implications?.map((imp: any) => ({
    audience: imp.audience,
    items: imp.items.map((item: any) => typeof item === 'string' ? { text: item } : item)
  }))
  
  if ((!implications || implications.length === 0) && sib?.persona_modules) {
    implications = sib.persona_modules.flatMap((pm: any) => 
      (pm.blocks || [])
        .filter((b: any) => b.block_type === 'insight')
        .map((b: any) => ({
          audience: pm.persona_mode?.replace(/_/g, ' ') || 'Strategic',
          items: [
            { text: b.title },
            { text: b.summary }
          ]
        }))
    )
  }

  // Build counterpoints from SIB global_blocks
  const counterpoints = (cig.claims || [])
    .filter((c: any) => c.counterpoint)
    .map((c: any) => ({
      claim: c.text,
      counterpoint: c.counterpoint!,
      strength: 'moderate' as const
    }))

  // Generate figures from SIB charts
  let figures = cig.figures?.map((fig: any, index: number) => ({
    id: fig.id,
    number: index + 1,
    title: fig.title,
    caption: fig.caption,
    type: fig.type
  }))

  if ((!figures || figures.length === 0) && sib?.charts) {
    figures = sib.charts.map((chart: any, index: number) => ({
      id: chart.chart_id,
      number: index + 1,
      title: chart.title,
      caption: chart.subtitle || chart.title,
      type: chart.chart_type
    }))
  }

  // Generate fallback markdown from SIB if not present
  let fallbackMarkdown = cig.raw_report_markdown
  if (!fallbackMarkdown && sib) {
    fallbackMarkdown = generateMarkdownFromSIB(sib)
  }

  return {
    tab: 'written_report',
    title: sib?.research_title || cig.metadata.title,
    subtitle: cig.metadata.subtitle,
    author: cig.metadata.author,
    last_updated: cig.metadata.last_updated,
    reading_time_minutes: cig.metadata.reading_time_minutes,
    word_count: cig.metadata.word_count,
    confidence: cig.metadata.confidence,
    bluf,
    executive_summary: executiveSummary,
    toc_enabled: true,
    figure_gallery_enabled: (figures?.length || 0) > 0,
    figures,
    sections,
    counterpoints,
    implications,
    methodology: cig.methodology,
    limitations: cig.limitations,
    footnotes,
    available_modes: ['clean', 'with_citations'],
    fallback_markdown: fallbackMarkdown,
    deep_research_markdown: deepResearchMarkdown
  }
}

/**
 * Build report sections from SIB data structure
 */
function buildSectionsFromSIB(sib: SIBData): any[] {
  const sections: any[] = []
  
  // Section 1: Executive Overview (from cockpit.status_strip and verdict)
  if (sib.cockpit?.status_strip || sib.cockpit?.verdict) {
    const overviewContent: any[] = []
    
    if (sib.cockpit.status_strip?.summary) {
      overviewContent.push({
        type: 'paragraph',
        content: sib.cockpit.status_strip.summary
      })
    }
    
    if (sib.cockpit.verdict?.summary) {
      overviewContent.push({
        type: 'callout',
        content: sib.cockpit.verdict.summary
      })
    }
    
    if (sib.cockpit.verdict?.payload?.recommendation) {
      overviewContent.push({
        type: 'paragraph',
        content: `**Recommendation:** ${sib.cockpit.verdict.payload.recommendation}`
      })
    }
    
    if (overviewContent.length > 0) {
      sections.push({
        id: 'executive-overview',
        title: 'Executive Overview',
        content_blocks: overviewContent,
        one_line_summary: sib.cockpit.verdict?.title
      })
    }
  }
  
  // Section 2: Key Signals (from global_blocks with block_type 'signal')
  const signalBlocks = sib.global_blocks?.filter((b: any) => b.block_type === 'signal') || []
  if (signalBlocks.length > 0) {
    sections.push({
      id: 'key-signals',
      title: 'Key Signals',
      content_blocks: signalBlocks.map((sig: any) => ({
        type: 'paragraph',
        content: `**${sig.title}**\n\n${sig.summary}`
      })),
      one_line_summary: `${signalBlocks.length} key signals identified`
    })
  }
  
  // NOTE: Comparison Matrix is NOT included in report sections because it has a dedicated 
  // standalone block renderer (generic_sib with sib_type='comparison_matrix'). Including it 
  // here would cause duplication in the UI.
  
  // Section 4: Scenario Analysis (from scenario_comparisons or scenario_comparison)
  const reportScenarioList = sib.scenario_comparisons || (sib.scenario_comparison ? [sib.scenario_comparison] : [])
  if (reportScenarioList.length > 0) {
    const scenarioContent: any[] = []
    
    for (const sc of reportScenarioList) {
      scenarioContent.push({
        type: 'heading',
        level: 3,
        content: sc.title
      })
      
      if (sc.notes) {
        scenarioContent.push({
          type: 'paragraph',
          content: sc.notes
        })
      }
      
      for (const scenario of sc.scenarios || []) {
        const name = scenario.name || scenario.label || 'Scenario'
        const prob = scenario.probability_band ?? scenario.probability_midpoint_pct ?? scenario.probability
        const probStr = typeof prob === 'number' ? `${Math.round(prob * 100)}%` : (prob || '—')
        scenarioContent.push({
          type: 'paragraph',
          content: `**${name}** (Probability: ${probStr})`
        })
        if (scenario.description) {
          scenarioContent.push({ type: 'paragraph', content: scenario.description })
        }
        if (scenario.assumptions && scenario.assumptions.length > 0) {
          scenarioContent.push({
            type: 'list',
            items: scenario.assumptions.slice(0, 5)
          })
        }
      }
    }
    
    sections.push({
      id: 'scenario-analysis',
      title: 'Scenario Analysis',
      content_blocks: scenarioContent
    })
  }
  
  // Section 5: Risk Assessment (from risk_matrices)
  if (sib.risk_matrices && sib.risk_matrices.length > 0) {
    const riskContent: any[] = []
    
    for (const rm of sib.risk_matrices) {
      for (const risk of rm.risks || []) {
        riskContent.push({
          type: 'paragraph',
          content: `**${risk.name}** (${risk.likelihood} likelihood, ${risk.impact} impact)\n\n${risk.description}\n\n*Mitigation:* ${risk.mitigation || 'TBD'}`
        })
      }
    }
    
    sections.push({
      id: 'risk-assessment',
      title: sib.risk_matrices[0].title || 'Risk Assessment',
      content_blocks: riskContent
    })
  }
  
  // Section 6: Strategic Recommendations (from persona_modules)
  if (sib.persona_modules && sib.persona_modules.length > 0) {
    const recContent: any[] = []
    
    for (const pm of sib.persona_modules) {
      for (const block of pm.blocks || []) {
        if (block.block_type === 'insight') {
          recContent.push({
            type: 'heading',
            level: 3,
            content: block.title
          })
          recContent.push({
            type: 'paragraph',
            content: block.summary
          })
        }
      }
    }
    
    if (recContent.length > 0) {
      sections.push({
        id: 'strategic-recommendations',
        title: 'Strategic Recommendations',
        content_blocks: recContent
      })
    }
  }
  
  // Section 7: Timeline (from timelines or timeline)
  const reportTimelineList = sib.timelines || (sib.timeline ? [sib.timeline] : [])
  if (reportTimelineList.length > 0) {
    const timelineContent: any[] = []
    
    for (const tl of reportTimelineList) {
      for (const [index, rawEvent] of (tl.events || []).entries()) {
        const event = normalizeTimelineEvent(rawEvent, index)
        const citationRefs = getTimelineCitationRefs(event)
        if (citationRefs.length === 0) {
          continue
        }
        const description = event.description ? `\n\n${event.description}` : ''

        timelineContent.push({
          type: 'paragraph',
          content: `**${event.date}** - ${event.title}${description}`,
          ...(citationRefs.length > 0 ? { citation_refs: citationRefs } : {}),
        })
      }
    }
    
    if (timelineContent.length > 0) {
      sections.push({
        id: 'timeline',
        title: reportTimelineList[0].title || 'Key Events Timeline',
        content_blocks: timelineContent
      })
    }
  }
  
  return sections
}

function normalizeDeepResearchSource(
  source: any,
  index: number
): { id: string; text: string; reliability?: { score: number; tier?: string; rationale?: string } } | null {
  if (!source) return null

  if (typeof source === 'string') {
    return {
      id: `deep_source_${index + 1}`,
      text: source,
      reliability: {
        score: 0.5,
        tier: 'secondary',
        rationale: 'Deep research supplemental source',
      },
    }
  }

  const id =
    source.source_id ||
    source.id ||
    source.url ||
    source.locator ||
    `deep_source_${index + 1}`

  const title = source.title || source.name || source.publication || source.publisher || 'Supplemental Source'
  const publisher = source.publisher || source.publication_name || source.provider
  const date = source.date || source.published_at
  const url = source.url || source.locator || source.link

  const pieces = [title]
  if (publisher) pieces.push(publisher)
  if (date) pieces.push(String(date))

  let text = pieces.filter(Boolean).join(' - ')
  if (url) {
    text += ` - ${url}`
  }

  const reliability = source.reliability || source.reliability_score

  return {
    id: String(id),
    text,
    reliability:
      reliability && typeof reliability === 'object'
        ? reliability
        : {
            score: typeof reliability === 'number' ? reliability : 0.6,
            tier: 'secondary',
            rationale: 'Deep research supplemental source',
          },
  }
}

/**
 * Generate fallback markdown from SIB data
 */
function generateMarkdownFromSIB(sib: SIBData): string {
  const parts: string[] = []
  
  // Title
  if (sib.research_title) {
    parts.push(`# ${sib.research_title}\n`)
  }
  
  // Executive Overview
  if (sib.cockpit?.verdict) {
    parts.push(`## Executive Summary\n`)
    if (sib.cockpit.verdict.title) {
      parts.push(`**Bottom Line:** ${sib.cockpit.verdict.title}\n`)
    }
    if (sib.cockpit.verdict.summary) {
      parts.push(`${sib.cockpit.verdict.summary}\n`)
    }
  }
  
  // Key Signals
  const signals = sib.global_blocks?.filter((b: any) => b.block_type === 'signal') || []
  if (signals.length > 0) {
    parts.push(`## Key Signals\n`)
    for (const sig of signals) {
      parts.push(`### ${sig.title}\n`)
      parts.push(`${sig.summary}\n`)
    }
  }
  
  // Metric Cards
  if (sib.metric_cards && sib.metric_cards.length > 0) {
    parts.push(`## Key Metrics\n`)
    for (const mc of sib.metric_cards) {
      parts.push(`- **${mc.title}:** ${mc.value} ${mc.unit || ''} ${mc.trend ? `(${mc.trend})` : ''}\n`)
    }
    parts.push('\n')
  }
  
  // Comparison Matrix
  if (sib.comparison_matrix) {
    parts.push(`## ${sib.comparison_matrix.title || 'Comparison'}\n`)
    
    // Helper to extract string value from cell (which may be object or string)
    const cellToStr = (cell: any): string => {
      if (cell === null || cell === undefined) return '—'
      if (typeof cell === 'string') return cell
      if (typeof cell === 'number') return String(cell)
      if (typeof cell === 'object') {
        if (cell.value !== undefined) {
          const val = typeof cell.value === 'string' ? cell.value : String(cell.value)
          const unit = cell.unit ? ` ${cell.unit}` : ''
          return `${val}${unit}`
        }
        if (cell.text !== undefined) return String(cell.text)
        if (cell.label !== undefined) return String(cell.label)
        if (cell.display !== undefined) return String(cell.display)
        return '—'
      }
      return String(cell)
    }
    
    // Build markdown table
    if (sib.comparison_matrix.column_headers && sib.comparison_matrix.row_headers && sib.comparison_matrix.cells) {
      const headers = ['', ...sib.comparison_matrix.column_headers]
      parts.push(`| ${headers.join(' | ')} |\n`)
      parts.push(`| ${headers.map(() => '---').join(' | ')} |\n`)
      
      for (let i = 0; i < sib.comparison_matrix.row_headers.length; i++) {
        const cells = sib.comparison_matrix.cells[i] || []
        const row = [sib.comparison_matrix.row_headers[i], ...cells.map(cellToStr)]
        parts.push(`| ${row.join(' | ')} |\n`)
      }
      parts.push('\n')
    }
  }
  
  // Risk Matrix
  if (sib.risk_matrices && sib.risk_matrices.length > 0) {
    parts.push(`## Risk Assessment\n`)
    for (const rm of sib.risk_matrices) {
      for (const risk of rm.risks || []) {
        parts.push(`### ${risk.name}\n`)
        parts.push(`- **Likelihood:** ${risk.likelihood}\n`)
        parts.push(`- **Impact:** ${risk.impact}\n`)
        parts.push(`${risk.description}\n`)
        if (risk.mitigation) {
          parts.push(`**Mitigation:** ${risk.mitigation}\n`)
        }
        parts.push('\n')
      }
    }
  }
  
  // Recommendations
  if (sib.persona_modules && sib.persona_modules.length > 0) {
    parts.push(`## Strategic Recommendations\n`)
    for (const pm of sib.persona_modules) {
      for (const block of pm.blocks || []) {
        if (block.block_type === 'insight') {
          parts.push(`### ${block.title}\n`)
          parts.push(`${block.summary}\n`)
        }
      }
    }
  }
  
  // Sources
  if (sib.sources_summary && sib.sources_summary.length > 0) {
    parts.push(`## Sources\n`)
    for (const src of sib.sources_summary) {
      parts.push(`- ${src.title}${src.reliability ? ` (Reliability: ${Math.round(src.reliability * 100)}%)` : ''}\n`)
    }
  }
  
  return parts.join('\n')
}

/**
 * Compile Takeaways view - can use SIB persona modules for insights
 */
function compileTakeawaysView(cig: CIG, _persona: Persona, _sib?: SIBData): TakeawaysView {
  return {
    tab: 'takeaways',
    key_findings: cig.key_findings.map((text: any, index: number) => ({
      text,
      importance: index < 3 ? 'critical' as const : index < 6 ? 'high' as const : 'medium' as const
    })),
    stakeholder_outcomes: buildStakeholderOutcomes(cig),
    decision_options_block: cig.decision_options && cig.decision_options.length > 0 ? {
      id: 'decision-options',
      type: 'decision_options',
      collapsed: false,
      data: {
        options: cig.decision_options
      }
    } as any : undefined,
    comparison_grid_block: undefined,
    premortem_checklist: cig.limitations?.map((lim: any, index: any) => ({
      risk: lim,
      mitigation: 'Assign owner and capture validation source.',
      probability: index === 0 ? 'high' as const : 'medium' as const
    })),
    watchlist_block: cig.watchlist && cig.watchlist.length > 0 ? {
      id: 'watchlist',
      type: 'watchlist',
      collapsed: false,
      data: {
        items: cig.watchlist,
        show_scenario_mapping: true
      }
    } as any : undefined
  }
}

/**
 * Compile Data & Figures view - USES SIB DATA for charts and tables
 * 
 * PRIORITY ORDER:
 * 1. SIB charts (rich interactive charts from Signal Intelligence Brief)
 * 2. CIG figures (only as fallback if no SIB charts)
 */
function hasNumericValue(value: unknown): value is number {
  if (typeof value === 'number' && Number.isFinite(value)) return true
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return true
  }
  return false
}

function normalizeChartDataPoint(point: any, fallbackIndex: number) {
  if (point == null) return null
  if (typeof point === 'number') {
    return { x: fallbackIndex, y: point }
  }
  if (typeof point === 'object') {
    const rawY = point.y ?? point.value ?? point.amount ?? point.score ?? point.percentage
    if (!hasNumericValue(rawY)) {
      return null
    }
    const rawX = point.x ?? point.category ?? point.label ?? point.name ?? fallbackIndex
    return {
      x: rawX,
      y: Number(rawY),
      label: point.label || point.name || point.category,
      source_id: point.source_id,
    }
  }
  return null
}

function normalizeSibChartSeries(series?: any[]): any[] {
  if (!Array.isArray(series)) return []
  return series
    .map((s, seriesIndex) => {
      const normalizedData = Array.isArray(s?.data)
        ? s.data
            .map((point: any, pointIndex: number) => normalizeChartDataPoint(point, pointIndex))
            .filter(Boolean)
        : []
      if (normalizedData.length === 0) return null
      return {
        series_id: s.series_id || `series_${seriesIndex + 1}`,
        name: s.name || `Series ${seriesIndex + 1}`,
        color: s.color,
        type: s.type,
        data: normalizedData,
      }
    })
    .filter(Boolean)
}

function hasTableContent(table: any): boolean {
  if (!table) return false
  const columns = Array.isArray(table.columns) ? table.columns.filter(Boolean) : []
  const rows = Array.isArray(table.rows) ? table.rows.filter(Boolean) : []
  return columns.length > 0 && rows.length > 0
}

function compileDataFiguresView(cig: CIG, persona: Persona, sib?: SIBData): DataFiguresView {
  const figures: any[] = []
  const datasets: any[] = []
  const figureNotes: string[] = []
  const datasetNotes: string[] = []
  let skippedCharts = 0
  let skippedTables = 0
  
  // ======= PRIORITY 1: Use SIB charts - ALWAYS prefer these =======
  console.log('[viewCompiler] compileDataFiguresView:', {
    hasSib: !!sib,
    sibCharts: sib?.charts?.length || 0,
    sibDataTables: sib?.data_tables?.length || 0,
    sibMetricCards: sib?.metric_cards?.length || 0,
    cigFigures: cig?.figures?.length || 0
  })
  
  if (sib?.charts && sib.charts.length > 0) {
    console.log('[viewCompiler] Using SIB charts for figures')
    
    // Build a source keyword index for auto-linking charts without explicit source_ids
    // Index both title keywords and snippet keywords for broader matching
    const sourceIndex: Array<{ id: string; titleKw: Set<string>; snippetKw: Set<string> }> = []
    if (cig.sources) {
      cig.sources.forEach((source: any) => {
        const sourceId = source.id || source.source_id || source.card_id
        if (!sourceId) return
        const title = (source.title || '').toLowerCase()
        const snippet = ((source as any).snippet || (source as any).summary || '').toLowerCase()
        const titleKw = new Set<string>(title.split(/\s+/).filter((w: string) => w.length > 3))
        const snippetKw = new Set<string>(snippet.split(/\s+/).filter((w: string) => w.length > 3))
        if (titleKw.size > 0 || snippetKw.size > 0) {
          sourceIndex.push({ id: sourceId, titleKw, snippetKw })
        }
      })
    }
    
    for (const chart of sib.charts) {
      const normalizedSeries = normalizeSibChartSeries(chart.series)
      if (normalizedSeries.length === 0) {
        skippedCharts += 1
        continue
      }
      
      let chartSourceIds = chart.source_ids || []
      const hasMissingSources = chart._missing_sources === true || chartSourceIds.length === 0
      
      if (chartSourceIds.length === 0 && sourceIndex.length > 0) {
        const chartParts = [
          chart.title || '',
          chart.subtitle || '',
          chart.x_axis_label || '',
          chart.y_axis_label || '',
          ...(normalizedSeries.map((s: any) => s.name || '')),
          ...(normalizedSeries.flatMap((s: any) =>
            (s.data || []).map((pt: any) => typeof pt.x === 'string' ? pt.x : ''))
          ),
        ]
        const chartWords = new Set(chartParts.join(' ').toLowerCase().split(/\s+/).filter((w: string) => w.length > 3))
        
        const scored: Array<{ score: number; id: string }> = []
        for (const entry of sourceIndex) {
          let titleHits = 0
          let snippetHits = 0
          for (const w of chartWords) {
            if (entry.titleKw.has(w)) titleHits++
            if (entry.snippetKw.has(w)) snippetHits++
          }
          const score = titleHits * 2 + snippetHits
          if (score >= 1) scored.push({ score, id: entry.id })
        }
        scored.sort((a, b) => b.score - a.score)
        const matched = scored.slice(0, 5).map(s => s.id)
        
        if (matched.length > 0) {
          console.log(`[viewCompiler] Auto-linked chart "${chart.title}" to ${matched.length} sources:`, matched.slice(0, 3))
          chartSourceIds = matched
        }
      }
      
      figures.push({
        id: chart.chart_id,
        number: figures.length + 1,
        type: chart.chart_type,
        title: chart.title,
        caption: chart.subtitle || chart.title,
        hasMissingSources, // Flag for frontend to show warning
        block: {
          id: `viz-${chart.chart_id}`,
          type: 'viz_frame',
          collapsed: false,
          data: {
            renderer: chart.chart_type,
            figure_id: chart.chart_id,
            inline_data: {
              type: chart.chart_type,
              series: normalizedSeries,
              interactive: chart.interactive ?? true,
              x_axis_label: chart.x_axis_label,
              y_axis_label: chart.y_axis_label,
              annotations: chart.annotations
            },
            caption: chart.subtitle || chart.title,
            // Pass through source IDs for evidence linking
            source_ids: chartSourceIds
          }
        } as any
      })
    }
  }
  
  // ======= Add SIB data tables =======
  if (sib?.data_tables && sib.data_tables.length > 0) {
    for (const dt of sib.data_tables) {
      if (!hasTableContent(dt)) {
        skippedTables += 1
        continue
      }
      datasets.push({
        id: dt.table_id,
        name: dt.title,
        description: dt.title,
        row_count: dt.rows?.length || 0,
        column_count: dt.columns?.length || 0,
        block: {
          id: `table-${dt.table_id}`,
          type: 'data_table',
          collapsed: false,
          data: {
            dataset_id: dt.table_id,
            columns: dt.columns?.map((c: any) => ({
              key: c.column_id,
              label: c.header,
              type: c.data_type,
              sortable: true,
              filterable: true
            })),
            rows: dt.rows?.map((r: any) => r.cells),
            enable_export: dt.exportable
          }
        } as any,
        download_formats: ['csv', 'json']
      })
    }
  }
  
  // ======= FALLBACK: Add CIG figures (ONLY if no SIB charts) =======
  if (figures.length === 0 && cig.figures && cig.figures.length > 0) {
    console.log('[viewCompiler] FALLBACK: No SIB charts, using CIG figures')
    for (const fig of cig.figures) {
      if (!fig?.data) continue
      figures.push({
        id: fig.id,
        number: figures.length + 1,
        type: fig.type,
        title: fig.title,
        caption: fig.caption,
        block: {
          id: `viz-${fig.id}`,
          type: 'viz_frame',
          collapsed: false,
          data: {
            renderer: fig.type,
            figure_id: fig.id,
            inline_data: fig.data,
            caption: fig.caption,
            sources: fig.data_source_ids
          }
        } as any
      })
    }
  }
  
  // ======= Add CIG datasets =======
  if (cig.datasets && cig.datasets.length > 0) {
    for (const ds of cig.datasets) {
      if (!hasTableContent(ds)) continue
      datasets.push({
        id: ds.id,
        name: ds.name,
        description: ds.description,
        row_count: ds.rows?.length || 0,
        column_count: ds.columns?.length || 0,
        block: {
          id: `table-${ds.id}`,
          type: 'data_table',
          collapsed: false,
          data: {
            dataset_id: ds.id,
            columns: ds.columns?.map((c: any) => ({
              key: c.key,
              label: c.label,
              type: c.type,
              sortable: true,
              filterable: true
            })),
            rows: ds.rows,
            enable_export: true
          }
        } as any,
        download_formats: ['csv', 'json']
      })
    }
  }
  
  // Build data dictionary from SIB metric_cards or CIG metrics
  let dataDictionary: any[] = []
  if (sib?.metric_cards && sib.metric_cards.length > 0) {
    dataDictionary = sib.metric_cards.map((mc: any) => ({
      metric_key: mc.metric_id,
      label: mc.title,
      definition: mc.context || `${mc.title} measured in ${mc.unit || 'units'}`,
      unit: mc.unit,
      value: mc.value,
      trend: mc.trend
    }))
  } else if (cig.metrics) {
    dataDictionary = cig.metrics.map((m: any) => ({
      metric_key: m.id,
      label: m.label,
      definition: `${m.label} measured in ${m.unit || 'units'}`,
      unit: m.unit
    }))
  }

  if (skippedCharts > 0) {
    figureNotes.push(
      `${skippedCharts} figure${skippedCharts === 1 ? '' : 's'} were skipped because they did not include chart data yet.`
    )
  }
  if (skippedTables > 0) {
    datasetNotes.push(
      `${skippedTables} table${skippedTables === 1 ? '' : 's'} were skipped because they had no rows or columns.`
    )
  }

  // ======= Compile stats_highlights and kpi_dashboards as SIB blocks =======
  const sibBlocks: any[] = []

  if (sib?.stats_highlights) {
    const items = Array.isArray(sib.stats_highlights) ? sib.stats_highlights : [sib.stats_highlights]
    for (const item of items) {
      if (!item || (typeof item === 'object' && Object.keys(item).length === 0)) continue
      sibBlocks.push({
        id: item.row_id || `stats-${sibBlocks.length}`,
        type: 'generic_sib',
        sibType: 'stats_highlights',
        title: item.title || 'Key Statistics',
        data: item,
      })
    }
  }

  if (sib?.kpi_dashboards) {
    const items = Array.isArray(sib.kpi_dashboards) ? sib.kpi_dashboards : [sib.kpi_dashboards]
    for (const item of items) {
      if (!item || (typeof item === 'object' && Object.keys(item).length === 0)) continue
      sibBlocks.push({
        id: item.dashboard_id || `kpi-${sibBlocks.length}`,
        type: 'generic_sib',
        sibType: 'kpi_dashboards',
        title: item.title || 'KPI Dashboard',
        data: item,
      })
    }
  }

  return {
    tab: 'data_figures',
    figures,
    datasets,
    data_dictionary: dataDictionary,
    figure_notes: figureNotes.length ? figureNotes : undefined,
    dataset_notes: datasetNotes.length ? datasetNotes : undefined,
    sib_blocks: sibBlocks.length > 0 ? sibBlocks : undefined,
    scenario_assumptions: cig.scenario_axes?.[0]?.assumptions ? 
      cig.scenario_axes[0].states.map((state: any) => ({
        state_id: state.state_id,
        state_label: state.label,
        assumptions: (cig.scenario_axes![0].assumptions || [])
          .filter((a: any) => !a.applies_to_state_id || a.applies_to_state_id === state.state_id)
          .map((a: any) => ({
            metric_key: a.id,
            base_value: 'Base',
            state_value: state.label,
            rationale: a.text
          }))
      })) : undefined
  }
}

/**
 * Compile Connections view
 * 
 * PRIORITY ORDER:
 * 1. SIB entity_graph (rich data from backend intelligence pipeline)
 * 2. CIG entities/relationships (frontend-extracted fallback)
 * 
 * The SIB entity_graph contains properly extracted entities with:
 * - importance scores (not hardcoded 50%)
 * - relationship edges with proper source/target
 * - claims and sources linked to entities
 */
function compileConnectionsView(cig: CIG, _persona: Persona, sib?: SIBData): ConnectionsView {
  const cigClaims = cig.claims || []
  const cigSources = cig.sources || []
  
  // Build source lookup map for enrichment
  const sourceMap = new Map<string, any>()
  cigSources.forEach((source: any) => {
    const sourceId = source.id || source.source_id || source.card_id
    if (sourceId) {
      sourceMap.set(sourceId, source)
    }
  })
  
  // Build claims lookup for fuzzy matching by entity name
  interface ClaimRecord {
    id: string
    text: string
    category: string
    confidence: string
    anchors: any[]
    textLower: string
  }
  const allClaims: ClaimRecord[] = []
  cigClaims.forEach((claim: any) => {
    const claimText = (claim.text || '').toLowerCase()
    allClaims.push({
      id: claim.id || claim.claim_id,
      text: claim.text,
      category: claim.category,
      confidence: claim.confidence,
      anchors: claim.evidence_anchors || claim.anchors || [],
      textLower: claimText
    })
  })
  
  // ======= PRIORITY 1: Use SIB entity_graph if available =======
  const sibGraph = sib?.entity_graph
  const cigRelationships = cig.relationships || []
  
  if (sibGraph && typeof sibGraph === 'object') {
    const sibNodes = sibGraph.nodes || []
    let sibEdges = sibGraph.edges || []
    
    // If SIB has no edges but CIG has relationships, use CIG relationships as edges
    if (sibEdges.length === 0 && cigRelationships.length > 0) {
      console.log('[viewCompiler] SIB entity_graph has no edges, using CIG relationships as fallback')
      sibEdges = cigRelationships.map((rel: any) => ({
        source_id: rel.source_entity_id || rel.source,
        target_id: rel.target_entity_id || rel.target,
        relationship: rel.relationship_type || rel.type || 'related_to',
        label: rel.description || rel.label,
        strength: rel.strength || 0.6,
        anchors: rel.anchors || rel.evidence_anchors || []
      }))
    }
    
    console.log('[viewCompiler] Using SIB entity_graph:', {
      nodes: sibNodes.length,
      edges: sibEdges.length
    })
    
    // Convert SIB nodes to ConnectionsView nodes
    const nodes = sibNodes.map((node: any, idx: number) => {
      // Handle various field name formats from backend
      const entityId = node.entity_id || node.node_id || node.id || `entity_${idx}`
      const entityName = node.name || node.label || `Entity ${idx + 1}`
      const entityType = (node.type || node.entity_type || 'concept').toLowerCase()
      
      // Get importance - backend provides this, don't default to 0.5
      const importance = typeof node.importance === 'number' ? node.importance : 
                        typeof node.relevance === 'number' ? node.relevance :
                        typeof node.weight === 'number' ? node.weight : 0.7
      
      // Find claims that mention this entity by name matching
      const entityClaims: any[] = []
      const entityNameLower = entityName.toLowerCase()
      allClaims.forEach((claim) => {
        if (claim.textLower.includes(entityNameLower)) {
          entityClaims.push({
            id: claim.id,
            text: claim.text,
            category: claim.category,
            confidence: claim.confidence,
            anchors: claim.anchors
          })
        }
      })
      
      // Find sources from claims that mention this entity
      const entitySources: any[] = []
      entityClaims.forEach((claim: any) => {
        (claim.anchors || []).forEach((anchor: any) => {
          const source = sourceMap.get(anchor.source_id)
          if (source && !entitySources.find(s => s.id === anchor.source_id)) {
            entitySources.push({
              id: source.id || source.source_id,
              title: source.title,
              url: source.url,
              snippet: anchor.snippet
            })
          }
        })
      })
      
      // Also check if node has direct source references
      if (node.sources && Array.isArray(node.sources)) {
        node.sources.forEach((srcRef: any) => {
          const srcId = typeof srcRef === 'string' ? srcRef : srcRef.source_id || srcRef.id
          const source = sourceMap.get(srcId)
          if (source && !entitySources.find(s => s.id === srcId)) {
            entitySources.push({
              id: source.id || source.source_id,
              title: source.title,
              url: source.url,
              snippet: srcRef.snippet
            })
          }
        })
      }
      
      return {
        id: entityId,
        label: entityName,
        type: entityType,
        description: node.description || node.summary || '',
        importance,
        group: entityType,
        claims: entityClaims,
        metrics: node.metrics || [],
        sources: entitySources,
        aliases: node.aliases || [],
        tags: node.tags || [],
        metadata: node.metadata || {}
      }
    })
    
    // Build entity ID set for edge validation
    // Include all ID variants (entity_id, node_id, id) since edges might reference any of them
    const entityIdSet = new Set<string>()
    sibNodes.forEach((node: any, idx: number) => {
      const primaryId = node.entity_id || node.node_id || node.id || `entity_${idx}`
      entityIdSet.add(primaryId)
      // Also add variants so edges using different ID formats still match
      if (node.entity_id) entityIdSet.add(node.entity_id)
      if (node.node_id) entityIdSet.add(node.node_id)
      if (node.id) entityIdSet.add(node.id)
    })
    
    // Create a map to resolve any ID variant to the primary node ID
    // ALSO map entity names and labels to IDs (LLM sometimes uses names instead of IDs in edges)
    const idToNodeId = new Map<string, string>()
    const nameToNodeId = new Map<string, string>() // For name-based edge matching
    sibNodes.forEach((node: any, idx: number) => {
      const primaryId = node.entity_id || node.node_id || node.id || `entity_${idx}`
      const entityName = (node.name || node.label || '').toLowerCase().trim()
      
      // Map all ID variants to primary ID
      if (node.entity_id) idToNodeId.set(node.entity_id, primaryId)
      if (node.node_id) idToNodeId.set(node.node_id, primaryId)
      if (node.id) idToNodeId.set(node.id, primaryId)
      idToNodeId.set(primaryId, primaryId)
      
      // Map entity name/label to primary ID (for name-based edge references)
      if (entityName) {
        nameToNodeId.set(entityName, primaryId)
        // Also add the raw name without lowercasing for exact matches
        nameToNodeId.set(node.name || node.label || '', primaryId)
      }
    })
    
    // Helper function to resolve an edge endpoint to a node ID
    const resolveToNodeId = (ref: string | undefined): string | null => {
      if (!ref) return null
      // First try direct ID lookup
      if (idToNodeId.has(ref)) return idToNodeId.get(ref)!
      // Then try name lookup (case-insensitive)
      const refLower = ref.toLowerCase().trim()
      if (nameToNodeId.has(refLower)) return nameToNodeId.get(refLower)!
      if (nameToNodeId.has(ref)) return nameToNodeId.get(ref)!
      // Check if any node name contains this ref or vice versa (fuzzy match)
      for (const [name, id] of nameToNodeId.entries()) {
        if (name.includes(refLower) || refLower.includes(name)) {
          return id
        }
      }
      return null
    }
    
    // Convert SIB edges to ConnectionsView edges
    let edges = sibEdges
      .map((edge: any, idx: number) => {
        const edgeId = edge.edge_id || edge.relationship_id || edge.id || `edge_${idx}`
        // Handle various field name formats (LLM outputs vary)
        // Common formats: source_id/target_id, source/target, from/to, from_node/to_node
        // Also handle entity names directly (LLM sometimes uses names instead of IDs)
        const rawSourceRef = edge.source_id || edge.source || edge.from || edge.from_node || edge.source_entity || edge.from_entity
        const rawTargetRef = edge.target_id || edge.target || edge.to || edge.to_node || edge.target_entity || edge.to_entity
        const relType = edge.relationship || edge.type || edge.relationship_type || edge.label || 'related_to'
        
        if (!rawSourceRef || !rawTargetRef) {
          console.log('[viewCompiler] Edge missing source/target refs:', edge)
          return null
        }
        
        // Resolve refs to node IDs (handles both IDs and entity names)
        const sourceId = resolveToNodeId(rawSourceRef)
        const targetId = resolveToNodeId(rawTargetRef)
        
        // Only include if both entities were resolved
        if (!sourceId || !targetId) {
          console.log('[viewCompiler] Could not resolve edge endpoints:', { rawSourceRef, rawTargetRef, resolvedSource: sourceId, resolvedTarget: targetId })
          return null
        }
        
        // Skip self-loops
        if (sourceId === targetId) {
          return null
        }
        
        return {
          id: edgeId,
          source: sourceId,
          target: targetId,
          type: relType,
          label: edge.label || edge.description || formatRelationshipLabel(relType),
          weight: edge.strength || edge.weight || 0.6,
          description: edge.description || '',
          anchors: edge.evidence_anchors || edge.anchors || []
        }
      })
      .filter((edge: any): edge is NonNullable<typeof edge> => edge !== null)
    
    // If we still have no edges but have nodes, try to generate relationships from CIG claims
    // This handles cases where the LLM failed to generate proper edge data
    if (edges.length === 0 && nodes.length >= 2) {
      console.log('[viewCompiler] No edges resolved, attempting to infer relationships from claims')
      const inferredEdges: any[] = []
      const seenEdgePairs = new Set<string>()
      
      // Look for entities that appear together in the same claim
      allClaims.forEach((claim) => {
        const mentionedNodeIds: string[] = []
        const claimTextLower = claim.textLower
        
        // Find all nodes mentioned in this claim
        nodes.forEach((node: any) => {
          const nodeName = (node.label || '').toLowerCase()
          if (nodeName && claimTextLower.includes(nodeName)) {
            mentionedNodeIds.push(node.id)
          }
        })
        
        // Create edges between all pairs of mentioned entities
        for (let i = 0; i < mentionedNodeIds.length; i++) {
          for (let j = i + 1; j < mentionedNodeIds.length; j++) {
            const pairKey = [mentionedNodeIds[i], mentionedNodeIds[j]].sort().join('|')
            if (!seenEdgePairs.has(pairKey)) {
              seenEdgePairs.add(pairKey)
              inferredEdges.push({
                id: `inferred_${inferredEdges.length}`,
                source: mentionedNodeIds[i],
                target: mentionedNodeIds[j],
                type: 'related_to',
                label: 'Related (from claims)',
                weight: 0.5,
                description: `Both entities mentioned in: "${claim.text.substring(0, 100)}..."`,
                anchors: claim.anchors || []
              })
            }
          }
        }
      })
      
      if (inferredEdges.length > 0) {
        console.log(`[viewCompiler] Inferred ${inferredEdges.length} relationships from claim co-occurrence`)
        edges = inferredEdges
      }
    }
    
    // LAST RESORT: Generate logical relationships based on entity types and context
    // When all else fails, create meaningful connections based on semantic analysis
    if (edges.length === 0 && nodes.length >= 2) {
      console.log('[viewCompiler] LAST RESORT: Generating relationships from entity types and semantic context')
      const generatedEdges: any[] = []
      const seenPairs = new Set<string>()
      
      // Find the primary/central entity (likely the research subject)
      // Usually the entity with the highest importance or a geographic entity mentioned first
      let primaryEntity: any = null
      const geographyNodes = nodes.filter((n: any) => 
        n.type === 'geography' || n.type === 'location' || n.type === 'region'
      )
      const organizationNodes = nodes.filter((n: any) => 
        n.type === 'organization' || n.type === 'institution'
      )
      const policyNodes = nodes.filter((n: any) => 
        n.type === 'policy' || n.type === 'regulation' || n.type === 'law'
      )
      const datasetNodes = nodes.filter((n: any) => 
        n.type === 'dataset' || n.type === 'data' || n.type === 'statistic'
      )
      const conceptNodes = nodes.filter((n: any) => 
        n.type === 'concept' || n.type === 'topic' || n.type === 'issue'
      )
      
      // Pick primary entity: highest importance geography, or first geography, or highest importance overall
      if (geographyNodes.length > 0) {
        primaryEntity = geographyNodes.reduce((best: any, node: any) => 
          (node.importance || 0) > (best?.importance || 0) ? node : best, geographyNodes[0])
      } else {
        primaryEntity = nodes.reduce((best: any, node: any) => 
          (node.importance || 0) > (best?.importance || 0) ? node : best, nodes[0])
      }
      
      console.log('[viewCompiler] Primary entity for hub:', primaryEntity?.label)
      
      // Helper to add edge without duplicates
      const addEdge = (source: any, target: any, type: string, label: string) => {
        if (source.id === target.id) return
        const pairKey = [source.id, target.id].sort().join('|')
        if (seenPairs.has(pairKey)) return
        seenPairs.add(pairKey)
        generatedEdges.push({
          id: `gen_${generatedEdges.length}`,
          source: source.id,
          target: target.id,
          type,
          label,
          weight: 0.6,
          description: `${source.label} ${label} ${target.label}`,
          anchors: []
        })
      }
      
      // 1. Connect all geography entities to each other (they're related locations)
      for (let i = 0; i < geographyNodes.length; i++) {
        for (let j = i + 1; j < geographyNodes.length; j++) {
          addEdge(geographyNodes[i], geographyNodes[j], 'located_in', 'is related to')
        }
      }
      
      // 2. Connect organizations to the primary geography
      if (primaryEntity) {
        organizationNodes.forEach((org: any) => {
          addEdge(org, primaryEntity, 'operates_in', 'operates in')
        })
        
        // 3. Connect policies to the primary geography
        policyNodes.forEach((policy: any) => {
          addEdge(policy, primaryEntity, 'applies_to', 'applies to')
        })
        
        // 4. Connect datasets to the primary geography
        datasetNodes.forEach((dataset: any) => {
          addEdge(dataset, primaryEntity, 'covers', 'provides data for')
        })
        
        // 5. Connect concepts to the primary geography
        conceptNodes.forEach((concept: any) => {
          addEdge(concept, primaryEntity, 'affects', 'is relevant to')
        })
      }
      
      // 6. Connect organizations to policies they might implement
      organizationNodes.forEach((org: any) => {
        policyNodes.forEach((policy: any) => {
          addEdge(org, policy, 'implements', 'implements')
        })
      })
      
      // 7. Connect datasets to organizations that might produce them
      organizationNodes.forEach((org: any) => {
        datasetNodes.forEach((dataset: any) => {
          addEdge(org, dataset, 'produces', 'produces')
        })
      })
      
      // 8. If we still don't have enough edges, connect all remaining nodes to primary hub
      if (generatedEdges.length < nodes.length - 1 && primaryEntity) {
        nodes.forEach((node: any) => {
          if (node.id !== primaryEntity.id) {
            addEdge(node, primaryEntity, 'related_to', 'is related to')
          }
        })
      }
      
      if (generatedEdges.length > 0) {
        console.log(`[viewCompiler] Generated ${generatedEdges.length} semantic relationships`)
        edges = generatedEdges
      }
    }
    
    const entityTypes: string[] = [...new Set(nodes.map((n: any) => n.type).filter(Boolean))] as string[]
    const relationshipTypes: string[] = [...new Set(edges.map((e: any) => e.type).filter(Boolean))] as string[]
    
    return {
      tab: 'connections',
      nodes,
      edges,
      entity_types: entityTypes,
      relationship_types: relationshipTypes,
      default_layout: 'force'
    }
  }
  
  // ======= FALLBACK: Use CIG entities/relationships =======
  console.log('[viewCompiler] No SIB entity_graph, falling back to CIG entities')
  
  const cigEntities = cig.entities || []
  // cigRelationships already declared above at start of function
  
  // Build lookup maps for enriched entity details
  const claimsByEntity = new Map<string, any[]>()
  const metricsByEntity = new Map<string, any[]>()
  
  // Map claims to entities they reference
  cigClaims.forEach((claim: any) => {
    const relatedEntities = claim.related_entities || claim.related_entity_ids || []
    relatedEntities.forEach((entityId: string) => {
      if (!claimsByEntity.has(entityId)) {
        claimsByEntity.set(entityId, [])
      }
      claimsByEntity.get(entityId)!.push({
        id: claim.id || claim.claim_id,
        text: claim.text,
        category: claim.category,
        confidence: claim.confidence,
        anchors: claim.evidence_anchors || claim.anchors || []
      })
    })
  })
  
  // Map metrics to entities (if metrics mention entity names)
  const cigMetrics = cig.metrics || []
  cigMetrics.forEach((metric: any) => {
    const metricLabel = (metric.label || metric.name || '').toLowerCase()
    cigEntities.forEach((entity: any) => {
      const entityName = (entity.name || '').toLowerCase()
      if (entityName && metricLabel.includes(entityName)) {
        const entityId = entity.id || entity.entity_id
        if (!metricsByEntity.has(entityId)) {
          metricsByEntity.set(entityId, [])
        }
        metricsByEntity.get(entityId)!.push({
          id: metric.id || metric.metric_id,
          label: metric.label || metric.name,
          value: metric.value,
          unit: metric.unit,
          trend: metric.trend
        })
      }
    })
  })
  
  // Normalize entities - handle both backend (entity_id, entity_type) and frontend (id, type) field names
  const nodes = cigEntities.map((entity: any, idx: number) => {
    const entityId = entity.id || entity.entity_id || `entity_${idx}`
    const entityName = entity.name || `Entity ${idx + 1}`
    const entityType = entity.type || entity.entity_type || 'concept'
    const entityNameLower = entityName.toLowerCase()
    
    // Get importance from entity or calculate from position
    const importance = typeof entity.importance === 'number' ? entity.importance :
                      typeof entity.relevance === 'number' ? entity.relevance :
                      Math.max(0.3, 1 - (idx * 0.05)) // Decrease by position if not specified
    
    // Collect sources that mention this entity via claims
    const entitySources: any[] = []
    const entityClaims = claimsByEntity.get(entityId) || []
    
    // Also do fuzzy matching by name for claims
    allClaims.forEach((claim) => {
      if (claim.textLower.includes(entityNameLower)) {
        const exists = entityClaims.some((c: any) => c.id === claim.id)
        if (!exists) {
          entityClaims.push({
            id: claim.id,
            text: claim.text,
            category: claim.category,
            confidence: claim.confidence,
            anchors: claim.anchors
          })
        }
      }
    })
    
    entityClaims.forEach((claim: any) => {
      (claim.anchors || []).forEach((anchor: any) => {
        const source = sourceMap.get(anchor.source_id)
        if (source && !entitySources.find(s => s.id === anchor.source_id)) {
          entitySources.push({
            id: source.id || source.source_id,
            title: source.title,
            url: source.url,
            snippet: anchor.snippet
          })
        }
      })
    })
    
    return {
      id: entityId,
      label: entityName,
      type: entityType,
      description: entity.description || '',
      importance,
      group: entityType,
      claims: entityClaims,
      metrics: metricsByEntity.get(entityId) || [],
      sources: entitySources,
      aliases: entity.aliases || [],
      tags: entity.tags || [],
      metadata: entity.metadata || {}
    }
  })

  // Build entity ID set for validation
  const entityIdSet = new Set(nodes.map(n => n.id))

  // Convert relationships to edges - handle both backend and frontend field names
  let edges = cigRelationships
    .map((rel: any, idx: number) => {
      const relId = rel.id || rel.relationship_id || `rel_${idx}`
      const sourceId = rel.source_entity_id || rel.source || rel.from
      const targetId = rel.target_entity_id || rel.target || rel.to
      const relType = rel.type || rel.relationship_type || 'related_to'
      
      if (!sourceId || !targetId) {
        return null
      }
      
      if (!entityIdSet.has(sourceId) || !entityIdSet.has(targetId)) {
        return null
      }
      
      return {
        id: relId,
        source: sourceId,
        target: targetId,
        type: relType,
        label: rel.label || rel.description || formatRelationshipLabel(relType),
        weight: rel.strength || rel.weight || 0.5,
        description: rel.description || '',
        anchors: rel.evidence_anchors || rel.anchors || []
      }
    })
    .filter((edge): edge is NonNullable<typeof edge> => edge !== null)
  
  // LAST RESORT for CIG fallback: Generate relationships if none exist
  if (edges.length === 0 && nodes.length >= 2) {
    console.log('[viewCompiler] CIG has no relationships, generating semantic connections')
    const generatedEdges: any[] = []
    const seenPairs = new Set<string>()
    
    const geographyNodes = nodes.filter(n => n.type === 'geography' || n.type === 'location')
    const organizationNodes = nodes.filter(n => n.type === 'organization' || n.type === 'institution')
    const policyNodes = nodes.filter(n => n.type === 'policy' || n.type === 'regulation')
    const datasetNodes = nodes.filter(n => n.type === 'dataset' || n.type === 'data')
    
    // Find primary entity
    let primaryEntity = geographyNodes.length > 0 
      ? geographyNodes.reduce((best, node) => (node.importance || 0) > (best?.importance || 0) ? node : best, geographyNodes[0])
      : nodes.reduce((best, node) => (node.importance || 0) > (best?.importance || 0) ? node : best, nodes[0])
    
    const addEdge = (source: any, target: any, type: string, label: string) => {
      if (source.id === target.id) return
      const pairKey = [source.id, target.id].sort().join('|')
      if (seenPairs.has(pairKey)) return
      seenPairs.add(pairKey)
      generatedEdges.push({
        id: `gen_${generatedEdges.length}`,
        source: source.id,
        target: target.id,
        type,
        label,
        weight: 0.6,
        description: `${source.label} ${label} ${target.label}`,
        anchors: []
      })
    }
    
    // Connect geography nodes
    for (let i = 0; i < geographyNodes.length; i++) {
      for (let j = i + 1; j < geographyNodes.length; j++) {
        addEdge(geographyNodes[i], geographyNodes[j], 'located_in', 'is related to')
      }
    }
    
    // Connect other types to primary
    if (primaryEntity) {
      organizationNodes.forEach(org => addEdge(org, primaryEntity, 'operates_in', 'operates in'))
      policyNodes.forEach(policy => addEdge(policy, primaryEntity, 'applies_to', 'applies to'))
      datasetNodes.forEach(dataset => addEdge(dataset, primaryEntity, 'covers', 'provides data for'))
    }
    
    // Connect organizations to policies
    organizationNodes.forEach(org => {
      policyNodes.forEach(policy => addEdge(org, policy, 'implements', 'implements'))
    })
    
    // Ensure all nodes are connected - hub and spoke as fallback
    if (generatedEdges.length < nodes.length - 1 && primaryEntity) {
      nodes.forEach(node => {
        if (node.id !== primaryEntity.id) {
          addEdge(node, primaryEntity, 'related_to', 'is related to')
        }
      })
    }
    
    if (generatedEdges.length > 0) {
      console.log(`[viewCompiler] Generated ${generatedEdges.length} semantic relationships for CIG fallback`)
      edges = generatedEdges
    }
  }

  const entityTypes = [...new Set(cigEntities.map((e: any) => e.type || e.entity_type).filter(Boolean))]
  const relationshipTypes = [...new Set(edges.map(e => e.type).filter(Boolean))]

  return {
    tab: 'connections',
    nodes,
    edges,
    entity_types: entityTypes,
    relationship_types: relationshipTypes,
    default_layout: 'force'
  }
}

/**
 * Format relationship type into human-readable label
 */
function formatRelationshipLabel(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())
}

/**
 * Compile Sources & Methods view - can use SIB sources_summary
 */
function compileSourcesMethodsView(cig: CIG, _persona: Persona, sib?: SIBData): SourcesMethodsView {
  const cigSources = cig.sources || []
  const cigClaims = cig.claims || []

  const claimSourceMap = new Map<string, string[]>()
  cigClaims.forEach((claim: any) => {
    const anchors = claim.evidence_anchors || []
    anchors.forEach((anchor: any) => {
      if (!anchor || typeof anchor.source_id !== 'string') return
      if (!claimSourceMap.has(anchor.source_id)) {
        claimSourceMap.set(anchor.source_id, [])
      }
      claimSourceMap.get(anchor.source_id)!.push(claim.id)
    })
  })

  const normalizeExcerpts = (excerpts?: any[], fallbackId?: string) => {
    if (!Array.isArray(excerpts) || excerpts.length === 0) return undefined
    const normalized = excerpts
      .map((excerpt: any, index: number) => {
        if (!excerpt) return null
        if (typeof excerpt === 'string') {
          return {
            id: `${fallbackId || 'excerpt'}-${index}`,
            text: excerpt,
            page: undefined
          }
        }
        return {
          id: excerpt.id || `${fallbackId || 'excerpt'}-${index}`,
          text: excerpt.text || excerpt.snippet || '',
          page: excerpt.page
        }
      })
      .filter(Boolean)
    return normalized.length > 0 ? normalized : undefined
  }

  const clampScore = (value?: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 0.8
    }
    return Math.min(1, Math.max(0, value))
  }
  
  // Generate contextual reliability rationale based on source properties
  // CRITICAL: Never fabricate data - only describe what we know from the source
  const generateReliabilityRationale = (source: any, score: number, tier?: string): string => {
    // Use existing rationale if available
    const existingRationale = source.reliability?.rationale || source.reliability_rationale || source.summary
    if (existingRationale && existingRationale.length > 30) {
      return existingRationale
    }
    
    // Build contextual description based on available metadata
    const parts: string[] = []
    const sourceType = source.source_type || source.lane || tier || ''
    const publisher = source.publisher || source.publication_name
    const title = source.title
    
    // Describe source type context
    const typeDescriptions: Record<string, string> = {
      'primary': 'Primary source providing direct evidence.',
      'academic': 'Academic or peer-reviewed publication.',
      'news': 'News media coverage and reporting.',
      'market': 'Market intelligence and financial data.',
      'data': 'Statistical data and official records.',
      'counterpoint': 'Alternative perspective or contrarian analysis.',
      'government_report': 'Official government documentation.',
      'peer_reviewed_study': 'Peer-reviewed academic research.',
      'market_report': 'Market analysis and financial intelligence.',
      'news_article': 'Journalistic reporting and news coverage.',
      'deep_research_synthesis': 'Synthesized deep research analysis.',
    }
    
    const normalizedType = sourceType.toLowerCase().replace(/_/g, '')
    for (const [key, desc] of Object.entries(typeDescriptions)) {
      if (normalizedType.includes(key.toLowerCase().replace(/_/g, ''))) {
        parts.push(desc)
        break
      }
    }
    
    // Add publisher context if available
    if (publisher) {
      parts.push(`Published by ${publisher}.`)
    }
    
    // Add reliability context based on score
    if (score >= 0.9) {
      parts.push('High reliability based on source authority.')
    } else if (score >= 0.7) {
      parts.push('Good reliability from established source.')
    } else if (score < 0.5) {
      parts.push('Lower reliability — verify with additional sources.')
    }
    
    // If we have enough context, return it
    if (parts.length > 0) {
      return parts.join(' ')
    }
    
    // Fallback for sources with no metadata
    return 'Source cited in research. Reliability assessment based on source category and publication type.'
  }

  const normalizedCigSources = cigSources.map((source: any, index: number) => {
    const id = source.id || source.source_id || `source-${index}`
    const reliabilityScore = clampScore(source.reliability?.score)
    const tier = source.reliability?.tier || source.source_type
    return {
      id,
      number: index + 1,
      title: source.title || `Source ${index + 1}`,
      publisher: source.publisher,
      authors: source.authors,
      date: source.date,
      url: source.url,
      source_type: source.source_type,
      reliability_score: reliabilityScore,
      reliability_tier: tier,
      reliability_rationale: generateReliabilityRationale(source, reliabilityScore, tier),
      claims_supported: claimSourceMap.get(id) || [],
      excerpts: normalizeExcerpts(source.excerpts, id)
    }
  })

  const sibSummary = Array.isArray(sib?.sources_summary) ? sib!.sources_summary : []
  const normalizedSibSources = sibSummary
    .map((source: any, index: number) => {
      const rawId = source?.source_id || source?.id || source?.card_id
      if (!rawId) return null
      const reliabilityScoreCandidate =
        typeof source.reliability === 'number'
          ? source.reliability
          : typeof source.reliability?.score === 'number'
            ? source.reliability.score
            : typeof source.reliability_score === 'number'
              ? source.reliability_score
              : typeof source.confidence === 'number'
                ? source.confidence
                : 0.75
      const normalizedScore = clampScore(reliabilityScoreCandidate)
      const excerptCandidates = source.excerpts || source.snippets || []
      const tier = source.reliability?.tier || source.lane || source.source_type

      return {
        id: rawId,
        number: cigSources.length + index + 1,
        title: source.title || source.publication_name || source.name || `Source ${rawId}`,
        publisher: source.publisher || source.publication || source.publication_name,
        authors: source.authors,
        date: source.date || source.published_at || source.published_date,
        url: source.url || source.locator,
        source_type: source.source_type || source.lane || 'other',
        reliability_score: normalizedScore,
        reliability_tier: tier,
        reliability_rationale: generateReliabilityRationale(source, normalizedScore, tier),
        claims_supported: Array.isArray(source.claim_ids) ? source.claim_ids : [],
        excerpts: normalizeExcerpts(excerptCandidates, rawId)
      }
    })
    .filter(Boolean) as any[]

  const mergedSources = () => {
    const seen = new Map<string, any>()
    const combined = [...normalizedCigSources, ...normalizedSibSources]
    combined.forEach(source => {
      if (!source?.id) return
      if (seen.has(source.id)) {
        const existing = seen.get(source.id)
        seen.set(source.id, {
          ...existing,
          ...source,
          url: existing.url || source.url,
          publisher: existing.publisher || source.publisher,
          authors: existing.authors || source.authors,
          reliability_score: clampScore(existing.reliability_score ?? source.reliability_score),
          reliability_rationale: existing.reliability_rationale || source.reliability_rationale,
          claims_supported: Array.from(new Set([...(existing.claims_supported || []), ...(source.claims_supported || [])])),
          excerpts: existing.excerpts && existing.excerpts.length > 0 ? existing.excerpts : source.excerpts
        })
      } else {
        seen.set(source.id, source)
      }
    })
    return Array.from(seen.values()).map((source, index) => ({
      ...source,
      number: index + 1,
      reliability_score: clampScore(source.reliability_score),
      reliability_rationale: source.reliability_rationale || 'Source referenced in intelligence run.',
      excerpts: source.excerpts
    }))
  }

  const sources = mergedSources()

  const evidenceLedger = cigClaims.map((claim: any) => {
    const anchors = claim.evidence_anchors || []
    return {
      claim_id: claim.id,
      claim_text: claim.text,
      source_ids: anchors.map((a: any) => a.source_id).filter(Boolean),
      support_types: Object.fromEntries(
        anchors
          .filter((a: any) => a?.source_id)
          .map((a: any) => [a.source_id, a.support_type])
      )
    }
  })
  
  return {
    tab: 'sources_methods',
    sources,
    evidence_ledger: evidenceLedger,
    methodology: cig.methodology ? {
      summary: cig.methodology
    } : undefined,
    limitations_block: cig.limitations && cig.limitations.length > 0 ? {
      id: 'limitations',
      type: 'limitations',
      collapsed: false,
      data: {
        limitations: cig.limitations.map((text: any, index: any) => ({
          id: `lim-${index}`,
          text,
          severity: index === 0 ? 'major' as const : 'moderate' as const
        })),
        open_questions: cig.open_questions
      }
    } as any : undefined,
    change_log_block: cig.change_log && cig.change_log.length > 0 ? {
      id: 'changelog',
      type: 'change_log',
      collapsed: false,
      data: {
        entries: cig.change_log
      }
    } as any : undefined
  }
}

// Helper functions
function confidenceToColor(confidence: string): 'green' | 'yellow' | 'red' | 'blue' | 'gray' {
  switch (confidence) {
    case 'very_high': return 'green'
    case 'high': return 'blue'
    case 'medium': return 'yellow'
    case 'low': return 'red'
    case 'very_low': return 'red'
    default: return 'gray'
  }
}

function riskToColor(risk: string): 'green' | 'yellow' | 'red' | 'blue' | 'gray' {
  switch (risk) {
    case 'critical': return 'red'
    case 'high': return 'red'
    case 'elevated': return 'yellow'
    case 'moderate': return 'yellow'
    case 'low': return 'blue'
    case 'minimal': return 'green'
    default: return 'gray'
  }
}

function convertFigureToVizBlock(figure: Figure): Block {
  return {
    id: `hero-${figure.id}`,
    type: 'viz_frame',
    collapsed: false,
    title: figure.title,
    data: {
      renderer: figure.type,
      inline_data: figure.data,
      figure_id: figure.id,
      caption: figure.caption,
      sources: figure.data_source_ids
    }
  } as Block
}

function buildAdditionalCockpitBlocks(cig: CIG): Block[] {
  const blocks: Block[] = []
  // Claim cards - handle undefined claims
  const cigClaims = cig.claims || []
  cigClaims.slice(0, 3).forEach((claim, index) => {
    blocks.push({
      id: `claim-${claim.id || `idx-${index}`}`,
      type: 'claim_card',
      collapsed: false,
      priority: 1 - index * 0.1,
      data: {
        claim: claim.text,
        why_it_matters: cig.key_findings[index] || '',
        counterpoint: claim.counterpoint,
        confidence: claim.confidence,
        evidence_anchors: claim.evidence_anchors,
        related_entity_ids: claim.related_entities
      }
    } as Block)
  })
  
  return blocks
}

function buildStakeholderOutcomes(cig: CIG) {
  const findings = cig.key_findings
  if (!findings.length) return undefined
  
  const templates = [
    { stakeholder: 'Leadership', tone: findings[0] },
    { stakeholder: 'Operators', tone: findings[1] || findings[0] },
    { stakeholder: 'External Stakeholders', tone: findings[2] || findings[0] }
  ]
  
  return templates.map(template => ({
    stakeholder: template.stakeholder,
    outcome: classifyOutcome(template.tone),
    explanation: template.tone
  }))
}

function classifyOutcome(text: string): 'winner' | 'loser' | 'neutral' | 'mixed' {
  const lower = text.toLowerCase()
  if (lower.match(/risk|loss|decline|concern/)) return 'loser'
  if (lower.match(/opportunity|growth|improve|gain/)) return 'winner'
  if (lower.match(/mixed|uncertain|depends/)) return 'mixed'
  return 'neutral'
}

function buildJumpChips(cig: CIG): { label: string; target_tab: 'cockpit' | 'written_report' | 'takeaways' | 'data_figures' | 'connections' | 'sources_methods'; target_section_id?: string }[] {
  const chips: { label: string; target_tab: 'cockpit' | 'written_report' | 'takeaways' | 'data_figures' | 'connections' | 'sources_methods'; target_section_id?: string }[] = [
    { label: 'Full Report', target_tab: 'written_report' },
    { label: 'Key Takeaways', target_tab: 'takeaways' },
    { label: 'Data & Figures', target_tab: 'data_figures' },
    { label: 'Sources', target_tab: 'sources_methods' }
  ]
  
  if (cig.report_sections && cig.report_sections.length > 0) {
    chips.push({
      label: truncate(cig.report_sections[0].title),
      target_tab: 'written_report',
      target_section_id: cig.report_sections[0].id
    })
  }
  
  return chips
}

function truncate(text: string, max = 24) {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function hasDataFigures(view: DataFiguresView): boolean {
  return (
    (view.figures?.length ?? 0) > 0 ||
    (view.datasets?.length ?? 0) > 0 ||
    (view.data_dictionary?.length ?? 0) > 0
  )
}



